/**
 * Blockchain Data Formatting Utilities
 * Handles Ethereum addresses, token amounts, scores, timestamps
 */

import { toast } from 'sonner';

// ============================================
// ETHEREUM ADDRESS FORMATTING
// ============================================

/**
 * Truncate Ethereum address to readable format
 * @param {string} address - Full Ethereum address
 * @param {number} startChars - Characters to show at start (default: 6)
 * @param {number} endChars - Characters to show at end (default: 4)
 * @returns {string} - Truncated address (e.g., "0x1234…abcd")
 */
export const formatAddress = (address, startChars = 6, endChars = 4) => {
  if (!address) return '—';
  if (address.length < startChars + endChars + 2) return address;
  
  return `${address.slice(0, startChars)}…${address.slice(-endChars)}`;
};

/**
 * Copy text to clipboard and show toast
 * @param {string} text - Text to copy
 * @param {string} label - Label for toast message
 */
export const copyToClipboard = async (text, label = 'Address') => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
    return true;
  } catch (error) {
    toast.error('Failed to copy to clipboard');
    return false;
  }
};

/**
 * Get block explorer URL for address/tx
 * @param {string} hash - Address or transaction hash
 * @param {string} type - 'address' or 'tx'
 * @param {string} network - Network name (default: 'anvil')
 * @returns {string} - Explorer URL
 */
export const getExplorerUrl = (hash, type = 'address', network = 'anvil') => {
  // For local Anvil, no explorer
  if (network === 'anvil' || network === 'localhost') {
    return null;
  }
  
  // Add mainnet/testnet explorers here when deploying
  const explorers = {
    ethereum: 'https://etherscan.io',
    polygon: 'https://polygonscan.com',
    arbitrum: 'https://arbiscan.io',
  };
  
  const baseUrl = explorers[network] || explorers.ethereum;
  return `${baseUrl}/${type}/${hash}`;
};

// ============================================
// TOKEN AMOUNT FORMATTING
// ============================================

/**
 * Format HERMES token amount from wei to human-readable
 * @param {string|number} weiAmount - Amount in wei (18 decimals)
 * @param {number} decimals - Token decimals (default: 18)
 * @param {number} displayDecimals - Decimals to show (default: 2)
 * @returns {string} - Formatted amount with symbol
 */
export const formatHermes = (weiAmount, decimals = 18, displayDecimals = 2) => {
  if (!weiAmount && weiAmount !== 0) return '0 HERMES';
  
  try {
    const amount = Number(weiAmount) / Math.pow(10, decimals);
    
    // Format with Intl.NumberFormat
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: displayDecimals,
      notation: amount >= 1000000 ? 'compact' : 'standard',
    }).format(amount);
    
    return `${formatted} HERMES`;
  } catch (error) {
    console.error('Error formatting HERMES amount:', error);
    return '0 HERMES';
  }
};

/**
 * Get full token amount for tooltip (no abbreviation)
 * @param {string|number} weiAmount - Amount in wei
 * @param {number} decimals - Token decimals (default: 18)
 * @returns {string} - Full formatted amount
 */
export const formatHermesFull = (weiAmount, decimals = 18) => {
  if (!weiAmount && weiAmount !== 0) return '0 HERMES';
  
  try {
    const amount = Number(weiAmount) / Math.pow(10, decimals);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(amount);
    
    return `${formatted} HERMES`;
  } catch (error) {
    return '0 HERMES';
  }
};

// ============================================
// SCORE FORMATTING
// ============================================

/**
 * Format tier score (0-100)
 * @param {number} score - Score value
 * @returns {string} - Formatted score
 */
export const formatScore = (score) => {
  if (score === null || score === undefined) return '—';
  return Math.round(score).toString();
};

/**
 * Get tier name from score
 * @param {number} score - Score value (0-100)
 * @returns {string} - Tier name
 */
export const getTierFromScore = (score) => {
  if (score === null || score === undefined) return 'Unknown';
  
  if (score >= 90) return 'Excellent';
  if (score >= 85) return 'Diamond';
  if (score >= 75) return 'Platinum';
  if (score >= 65) return 'Gold';
  if (score >= 50) return 'Silver';
  return 'Bronze';
};

/**
 * Map backend tier names to frontend display names
 * Backend uses 0-1000 scale with: excellent, great, good, warning, poor, unacceptable
 * @param {string} backendTier - Tier name from backend
 * @returns {string} - Frontend display tier name
 */
export const mapBackendTierToFrontend = (backendTier) => {
  if (!backendTier) return 'Unknown';
  
  const tierMap = {
    'excellent': 'Excellent',
    'great': 'Diamond',
    'good': 'Platinum',
    'warning': 'Gold',
    'poor': 'Silver',
    'unacceptable': 'Bronze'
  };
  
  return tierMap[backendTier.toLowerCase()] || backendTier;
};

/**
 * Get tier color classes from tier name
 * @param {string} tier - Tier name
 * @returns {string} - Tailwind classes
 */
export const getTierColorClasses = (tier) => {
  const tierClasses = {
    'Excellent': 'bg-[hsl(var(--tier-excellent))]/15 text-[hsl(var(--tier-excellent))] border border-[hsl(var(--tier-excellent))]/30',
    'Diamond': 'bg-[hsl(var(--tier-diamond))]/15 text-[hsl(var(--tier-diamond))] border border-[hsl(var(--tier-diamond))]/30',
    'Platinum': 'bg-[hsl(var(--tier-platinum))]/15 text-[hsl(var(--tier-platinum))] border border-[hsl(var(--tier-platinum))]/30',
    'Gold': 'bg-[hsl(var(--tier-gold))]/15 text-[hsl(var(--tier-gold))] border border-[hsl(var(--tier-gold))]/30',
    'Silver': 'bg-[hsl(var(--tier-silver))]/15 text-[hsl(var(--tier-silver))] border border-[hsl(var(--tier-silver))]/30',
    'Bronze': 'bg-[hsl(var(--tier-bronze))]/15 text-[hsl(var(--tier-bronze))] border border-[hsl(var(--tier-bronze))]/30',
  };
  
  return tierClasses[tier] || 'bg-muted text-muted-foreground';
};

/**
 * Calculate score delta and format
 * @param {number} current - Current score
 * @param {number} previous - Previous score
 * @returns {object} - { delta: number, formatted: string, isPositive: boolean }
 */
export const calculateScoreDelta = (current, previous) => {
  if (!current || !previous) {
    return { delta: 0, formatted: '—', isPositive: null };
  }
  
  const delta = current - previous;
  const isPositive = delta > 0;
  const formatted = `${isPositive ? '+' : ''}${delta.toFixed(0)}`;
  
  return { delta, formatted, isPositive };
};

// ============================================
// TIMESTAMP FORMATTING
// ============================================

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 * @param {string|number|Date} timestamp - Timestamp
 * @returns {string} - Relative time
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '—';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    
    return date.toLocaleDateString();
  } catch (error) {
    return '—';
  }
};

/**
 * Format timestamp to full datetime
 * @param {string|number|Date} timestamp - Timestamp
 * @returns {string} - Full datetime string
 */
export const formatFullDatetime = (timestamp) => {
  if (!timestamp) return '—';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '—';
  }
};

// ============================================
// PERCENTAGE FORMATTING
// ============================================

/**
 * Format APY/percentage
 * @param {number} value - Percentage value
 * @param {number} decimals - Decimals to show (default: 2)
 * @returns {string} - Formatted percentage
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format basis points to percentage
 * @param {number} bps - Basis points (1 bps = 0.01%)
 * @returns {string} - Formatted percentage
 */
export const formatBasisPoints = (bps) => {
  if (bps === null || bps === undefined) return '—';
  const percent = bps / 100;
  return `${percent.toFixed(2)}%`;
};

// ============================================
// VALIDATION
// ============================================

/**
 * Validate Ethereum address format
 * @param {string} address - Address to validate
 * @returns {boolean} - True if valid
 */
export const isValidAddress = (address) => {
  if (!address) return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Validate hotel ID format
 * @param {string} hotelId - Hotel ID to validate
 * @returns {boolean} - True if valid
 */
export const isValidHotelId = (hotelId) => {
  if (!hotelId) return false;
  // UUIDs or MongoDB ObjectIds
  return /^[a-f0-9\-]{24,36}$/i.test(hotelId);
};
